/*
 *  ************************************************************************
 *  *  © [2015 - 2024] Quintype Technologies India Private Limited
 *  *  All Rights Reserved.
 *  *************************************************************************
 */
import React, { useState } from 'react';
import { Button } from '../../atoms/button';
import { useDispatch, useSelector } from 'react-redux';
import { CustomHeadline } from '../../atoms/custom-headline';
import {
  ENTER_PASSWORD,
  ENTER_PASSWORD_TITLE,
  FORGOT_PASSWORD_LINK,
  UPDATE_SIGN_IN,
  UPDATE_SINGLE_SIGN_ON,
  VERIFY_SIGN_IN_BUTTON,
  SIGN_IN_SUCCESS
} from '../../../../../constants';
import { ErrorMessage } from '../../atoms/error-message';
import {
  getErrorStyle,
  triggerLogin,
  genericError,
  triggerSSO,
  triggerSendOtp,
  inputFieldError,
  handleScrollToTop
} from '../../util';
import get from 'lodash/get';
import visibilityIcon from '../../../../../../assets/icons/visibility.svg';
import visibilityOffIcon from '../../../../../../assets/icons/visibility-off.svg';
import './password.module.css';
import { SvgHandler } from '../../../../svg-handler/svg-handler';
import { checkIfSingleSignOn } from '../../sso-utils';
import { SuccessMessage } from '../../atoms/success-message';
import { getPath } from '../../../../../utils';

export const PasswordScreen = ({ mode, isUserLoginPage = false, displayData = '' }) => {
  const { general = {}, login = {}, sessionLimit={} } = useSelector(state => get(state, ['qt', 'config', 'pagebuilder-config'], {}));

  const sketchesHost = useSelector(state => get(state, ['qt', 'config', 'sketches-host'], {}));
  const whiteListedUrlParams = useSelector(state =>
    get(state, ['qt', 'config', 'pagebuilder-config', 'general', 'others', 'whiteListedUrlParams'], [])
  );
  const { currentPath = '', currentHostUrl = '' } = useSelector(state => get(state, ['qt'], {}));
  const { loginSuccess = '' } = get(login, ['pageSetting'], {});
  const { button, enterPasswordTitle, enterPasswordLabel, forgotPasswordLink } = get(
    login,
    ['enter-password', 'rows', 0, 'config'],
    {}
  );
  const { sso = {} } = general;
  const [inputValue, setInputValue] = useState('');
  const [response, setResponse] = useState(null);
  const [api, setApi] = useState(null);
  const [hidePassword, setPassword] = useState(true);
  const [displaySuccess, setSuccess] = useState(false);
  const [disableButton, setDisableButton] = useState(false)
  const dispatch = useDispatch();
  const { signIn = {}, singleSignOn = {} } = useSelector(state => get(state, ['login'], {}));
  const { inputData } = get(signIn, ['userData'], {});
  const { inputData: ssoInputData } = get(singleSignOn, ['userData'], {});
  const email = !isUserLoginPage ? inputData : ssoInputData;
  const triggerOnEdit = () =>
    dispatch({ type: !isUserLoginPage ? UPDATE_SIGN_IN : UPDATE_SINGLE_SIGN_ON, payload: { currentScreen: 'signIn' } });

  handleScrollToTop();

  const triggerHandler = async () => {
    try {
      const loginProps = { data: email, credential: inputValue, type: 'password', sketchesHost };
      const { responseStatus: passwordResponseStatus, qtAuthCookie, verificationStatus } = await triggerLogin(loginProps);
      setResponse(passwordResponseStatus);
      setApi('login-email');
      if (passwordResponseStatus === 200) {
        if (!verificationStatus) {
          const otpResponse = await triggerSendOtp({ email, sketchesHost });
          setResponse(otpResponse);
          setApi('send-otp-email');
          if (otpResponse === 200) {
            dispatch({
              type: !isUserLoginPage ? UPDATE_SIGN_IN : UPDATE_SINGLE_SIGN_ON,
              payload: {
                currentScreen: !isUserLoginPage ? 'otp' : 'mobileOtp',
                userData: { inputData: email, countryCode: '', qtAuthCookie: qtAuthCookie, updateOtp: true }
              }
            });
          }
        } else {
          if (isUserLoginPage && checkIfSingleSignOn(sso) && window?.location) {
            const ssoProps = { sso: sso, currentHostUrl: currentHostUrl, currentPath: currentPath, sketchesHost };
            triggerSSO(ssoProps);
          }
          global?.localStorage?.removeItem('userEmail');
          setSuccess(true);
          setDisableButton(true);
        }
      }
    } catch (error) {
      setResponse('error');
      setApi('login-email');
    }
  };

  const triggerForgotPasswordLink = () => {
    if (!isUserLoginPage) {
      window.location.href = getPath({
        sketchesHost,
        relativePath: '/forgot-password',
        injectCustomParams: true,
        whiteListedUrlParams
      });
    } else {
      dispatch({
        type: UPDATE_SINGLE_SIGN_ON,
        payload: { currentScreen: 'forgotPassword', userData: { inputData: email } }
      });
    }
  };
  const wrapperStyle = displaySuccess ? `auth--wrapper ${mode} success-wrapper` : `auth--wrapper ${mode}`;
  return (
    <div className={wrapperStyle}>
      <CustomHeadline
        headline={enterPasswordTitle || ENTER_PASSWORD_TITLE}
        editData={displayData || email}
        onEdit={triggerOnEdit}
        id='password-headline'
      />
      <div className={getErrorStyle(response, api, mode)} styleName='input-wrapper'>
        <label htmlFor='password'>{enterPasswordLabel || ENTER_PASSWORD}</label>
        <input
          type={hidePassword ? 'password' : 'text'}
          value={inputValue}
          id='password'
          onChange={event => {
            setResponse(null);
            setApi(null);
            setInputValue(event.target.value);
          }}
          name='password'
          required
        />
        <div styleName={'eye-wrapper'} onClick={() => setPassword(!hidePassword)}>
          <SvgHandler
            xlinkHref={hidePassword ? visibilityIcon : visibilityOffIcon}
            width='24'
            height='24'
            viewBox='0 0 24 24'
            invertColor={mode === 'dark'}
          />
        </div>
        {inputFieldError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
        <div styleName='action-label' className='auth--text-color' onClick={() => triggerForgotPasswordLink()}>
          {forgotPasswordLink || FORGOT_PASSWORD_LINK}
        </div>
      </div>
      <Button
        text={button || VERIFY_SIGN_IN_BUTTON}
        clickHandler={() => triggerHandler()}
        disable={!inputValue || disableButton}
        enableEnterClick={true}
        enableLoader={true}
      />
      {genericError(response, api) ? <ErrorMessage response={response} api={api} mode={mode} /> : <></>}
      {displaySuccess ? (
        <SuccessMessage message={loginSuccess || SIGN_IN_SUCCESS} mode={mode} isUserLoginPage={isUserLoginPage} sessionLimit={sessionLimit}/>
      ) : (
        <></>
      )}
    </div>
  );
};
